import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Copy,
  Check,
  Radio,
  Eye,
  EyeOff,
  Trash2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";

const COPY_ROWS = [
  { key: "cf_live_rtmps_server", label: "RTMPS Server", secret: false, testKey: "rtmps-server" },
  { key: "cf_live_rtmps_key",    label: "Stream Key",   secret: true,  testKey: "rtmps-key" },
  { key: "cf_live_playback_hls", label: "HLS Playback", secret: false, testKey: "playback-hls" },
  { key: "cf_live_iframe_url",   label: "Iframe Player", secret: false, testKey: "iframe" },
];

/**
 * Displays a channel's Cloudflare Stream Live Input credentials with per-row
 * copy buttons + step-by-step OBS Studio setup guidance. If no live input
 * is linked yet, shows a "Paste your Cloudflare creds" form.
 */
export default function ObsIngestPanel({ channel, onUpdated }) {
  const [expanded, setExpanded] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [linkForm, setLinkForm] = useState({
    live_input_id: "",
    rtmps_key: "",
    playback_key: "",
    iframe_url: "",
  });
  const [saving, setSaving] = useState(false);

  const linked = !!channel?.cf_live_input_id;

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success("Copied");
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {
      toast.error("Copy failed — select and Ctrl+C");
    }
  };

  const link = async () => {
    if (!linkForm.live_input_id.trim() || !linkForm.rtmps_key.trim()) {
      toast.error("Live Input ID + RTMPS Stream Key are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/channels/${channel.id}/live-input`, {
        live_input_id: linkForm.live_input_id.trim(),
        rtmps_key: linkForm.rtmps_key.trim(),
        playback_key: linkForm.playback_key.trim() || undefined,
        iframe_url: linkForm.iframe_url.trim() || undefined,
      });
      toast.success("Cloudflare Live Input linked — start streaming from OBS");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    }
    setSaving(false);
  };

  const unlink = async () => {
    if (!window.confirm("Disconnect Cloudflare Live Input? OBS pushes will stop reaching viewers.")) return;
    try {
      const r = await api.delete(`/channels/${channel.id}/live-input`);
      toast.success("Live Input disconnected");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  return (
    <section
      className="border border-border rounded-md bg-card overflow-hidden"
      data-testid="obs-ingest-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Radio className={`w-4 h-4 ${linked ? "text-emerald-400" : "text-muted-foreground"}`} strokeWidth={1.75} />
          <div className="text-left">
            <div className="font-display font-bold text-sm">OBS / Live Ingest</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {linked
                ? `Cloudflare Stream · ${channel.cf_live_input_id.slice(0, 8)}… · RTMPS ready`
                : "Not linked · paste Cloudflare Live Input creds to enable"}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="border-t border-border p-4 space-y-3">
          {linked ? (
            <>
              {COPY_ROWS.map((row) => {
                const value = channel[row.key] || "";
                if (!value) return null;
                const isKey = row.secret && !showKey;
                const display = isKey ? "•".repeat(Math.min(48, value.length)) : value;
                return (
                  <div key={row.key} data-testid={`obs-row-${row.testKey}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{row.label}</Label>
                      {row.secret && (
                        <button
                          type="button"
                          onClick={() => setShowKey((v) => !v)}
                          className="text-[10px] font-mono text-muted-foreground hover:text-foreground flex items-center gap-1"
                          data-testid="obs-toggle-key"
                        >
                          {showKey ? <><EyeOff className="w-3 h-3" /> hide</> : <><Eye className="w-3 h-3" /> reveal</>}
                        </button>
                      )}
                    </div>
                    <div className="flex items-stretch gap-1">
                      <Input
                        readOnly
                        value={display}
                        className="rounded-sm font-mono text-[11px] bg-muted/40"
                        onFocus={(e) => e.target.select()}
                        data-testid={`obs-value-${row.testKey}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copy(row.key, value)}
                        className="rounded-sm shrink-0 h-auto"
                        title={`Copy ${row.label}`}
                        data-testid={`obs-copy-${row.testKey}`}
                      >
                        {copiedKey === row.key ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}

              <div className="rounded-sm bg-emerald-500/5 border border-emerald-500/30 px-3 py-2 text-[11px] leading-relaxed" data-testid="obs-setup-instructions">
                <div className="font-display font-bold text-emerald-400 text-xs mb-1">OBS Studio setup — 60 seconds</div>
                <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
                  <li>Open <b className="text-foreground">OBS Studio → Settings → Stream</b></li>
                  <li>Service: <span className="font-mono text-foreground">Custom…</span></li>
                  <li>Server: paste the <b className="text-foreground">RTMPS Server</b> above</li>
                  <li>Stream Key: paste the <b className="text-foreground">Stream Key</b> above</li>
                  <li>Click <b className="text-foreground">Apply</b> → <b className="text-foreground">OK</b> → <b className="text-foreground">Start Streaming</b></li>
                  <li>Viewers watch instantly via the HLS Playback URL on any HLS-capable player, or embed the Iframe player above.</li>
                </ol>
              </div>

              {channel.cf_live_iframe_url && (
                <a
                  href={channel.cf_live_iframe_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] font-mono text-fuchsia-400 hover:text-fuchsia-300 inline-flex items-center gap-1"
                  data-testid="obs-open-iframe"
                >
                  <ExternalLink className="w-3 h-3" /> Open Cloudflare iframe player
                </a>
              )}

              <div className="rounded-sm bg-amber-500/5 border border-amber-500/30 px-3 py-2 text-[11px] leading-relaxed" data-testid="obs-scope-note">
                <div className="font-display font-bold text-amber-400 text-xs mb-1">This feeds the app — not your Flussonic output</div>
                <p className="text-muted-foreground">
                  OBS here streams <b className="text-foreground">into</b> this channel (the app's Cloudflare Live Input).
                  It does <b className="text-foreground">not</b> publish to your outbound CDN. Don't point OBS at your
                  Flussonic push URL (e.g. <span className="font-mono">…/static/AFROMUSICHD</span>) — that slot is driven by the
                  platform's push worker, and a second publisher there will be rejected.
                </p>
              </div>

              <div className="pt-2 border-t border-border">
                <Button
                  variant="outline"
                  onClick={unlink}
                  className="rounded-sm border-rose-500/40 text-rose-500 hover:bg-rose-500/10 text-xs"
                  data-testid="obs-unlink"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-muted-foreground">
                Create a <b className="text-foreground">Live Input</b> at{" "}
                <a href="https://dash.cloudflare.com/?to=/:account/stream/inputs" target="_blank" rel="noopener noreferrer" className="text-fuchsia-400 hover:text-fuchsia-300 underline">
                  Cloudflare Dashboard → Stream → Live Inputs
                </a>{" "}
                and paste the returned credentials here.
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Live Input ID</Label>
                <Input
                  className="rounded-sm mt-1 font-mono text-xs"
                  placeholder="f0bd1a18efa7447b9476157a0814036b"
                  value={linkForm.live_input_id}
                  onChange={(e) => setLinkForm((s) => ({ ...s, live_input_id: e.target.value }))}
                  data-testid="obs-form-live-input-id"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">RTMPS Stream Key</Label>
                <Input
                  className="rounded-sm mt-1 font-mono text-xs"
                  placeholder="4bfa…"
                  value={linkForm.rtmps_key}
                  onChange={(e) => setLinkForm((s) => ({ ...s, rtmps_key: e.target.value }))}
                  data-testid="obs-form-rtmps-key"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">RTMPS Playback Key (optional)</Label>
                <Input
                  className="rounded-sm mt-1 font-mono text-xs"
                  value={linkForm.playback_key}
                  onChange={(e) => setLinkForm((s) => ({ ...s, playback_key: e.target.value }))}
                  data-testid="obs-form-playback-key"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Iframe Player URL (optional)</Label>
                <Input
                  className="rounded-sm mt-1 font-mono text-xs"
                  placeholder="https://customer-…cloudflarestream.com/…/iframe"
                  value={linkForm.iframe_url}
                  onChange={(e) => setLinkForm((s) => ({ ...s, iframe_url: e.target.value }))}
                  data-testid="obs-form-iframe-url"
                />
              </div>
              <Button
                onClick={link}
                disabled={saving}
                className="rounded-sm tenant-btn text-xs"
                data-testid="obs-link-btn"
              >
                {saving ? "Linking…" : "Link Cloudflare Live Input"}
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
